import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calendar, DollarSign, Clock, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
  start_date: string;
  due_date: string;
  budget: number;
  created_at: string;
}

export const ClientProjects = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "planning":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "completed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "on-hold":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const getProgress = (status: string) => {
    switch (status.toLowerCase()) {
      case "planning": return 25;
      case "active": return 60;
      case "completed": return 100;
      case "on-hold": return 50;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
        <p className="text-muted-foreground mt-4">Loading projects...</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <Card className="glass-card p-8 sm:p-12 text-center">
        <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold mb-2">No Projects Yet</h3>
        <p className="text-sm sm:text-base text-muted-foreground">
          Projects will appear here once our team starts working on your requests.
        </p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:gap-6">
      {projects.map((project) => (
        <Card key={project.id} className="glass-card-hover p-4 sm:p-6 transition-all hover-scale">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold mb-2 truncate">{project.name}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 line-clamp-2">
                {project.description}
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                {project.start_date && (
                  <span className="flex items-center gap-1 shrink-0">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Started: </span>
                    {new Date(project.start_date).toLocaleDateString()}
                  </span>
                )}
                {project.due_date && (
                  <span className="flex items-center gap-1 shrink-0">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Due: </span>
                    {new Date(project.due_date).toLocaleDateString()}
                  </span>
                )}
                {project.budget && (
                  <span className="flex items-center gap-1 shrink-0">
                    <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                    ${project.budget.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <Badge className={`${getStatusColor(project.status)} shrink-0 capitalize text-xs sm:text-sm`}>
              {project.status === "completed" && <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />}
              {project.status}
            </Badge>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{getProgress(project.status)}%</span>
            </div>
            <Progress value={getProgress(project.status)} className="h-2" />
          </div>
        </Card>
      ))}
    </div>
  );
};
